export type GtmEventPayload = {
  event: string;
  product_type?: string;
  conversion_value?: number;
  currency?: string;
  step?: number;
  step_name?: string;
  bank_authorization?: boolean;
  [key: string]: string | number | boolean | undefined;
};

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

const PENDING_BANK_VERIFICATION_KEY = 'gtm_pending_bank_verification';

const STEP_NAMES: Record<number, string> = {
  1: 'personal_info',
  2: 'loan_details',
  3: 'address_info',
  4: 'family_info',
  5: 'bank_verification',
};

export const ensureDataLayer = (): void => {
  window.dataLayer = window.dataLayer || [];
};

export const pushGtmEvent = (payload: GtmEventPayload): void => {
  ensureDataLayer();
  window.dataLayer!.push({ ...payload });
};

export const getLoanFormGtmPayload = (formData: {
  loanType?: string;
  loanAmount?: number | string;
}) => ({
  product_type: formData.loanType || '',
  conversion_value: Number(formData.loanAmount) || 0,
  currency: 'EUR' as const,
});

export const trackFormStart = (formData: { loanType?: string; loanAmount?: number | string }): void => {
  pushGtmEvent({
    event: 'form_start',
    ...getLoanFormGtmPayload(formData),
  });
};

export const trackFormStepCompleted = (
  step: number,
  formData: { loanType?: string; loanAmount?: number | string }
): void => {
  pushGtmEvent({
    event: 'form_step_completed',
    step,
    step_name: STEP_NAMES[step] || `step_${step}`,
    ...getLoanFormGtmPayload(formData),
  });
};

export const trackFormStepBack = (
  step: number,
  formData: { loanType?: string; loanAmount?: number | string }
): void => {
  pushGtmEvent({
    event: 'form_step_back',
    step,
    step_name: STEP_NAMES[step] || `step_${step}`,
    ...getLoanFormGtmPayload(formData),
  });
};

export const trackFormSubmittedUnverified = (
  formData: {
    loanType?: string;
    loanAmount?: number | string;
    selectedBank?: string;
  },
  options: { goingToBankVerification?: boolean } = {}
): void => {
  const goingToBankVerification = Boolean(options.goingToBankVerification);
  const payload = {
    ...getLoanFormGtmPayload(formData),
    bank_authorization: goingToBankVerification,
    bank_id: goingToBankVerification ? formData.selectedBank || '' : '',
  };

  pushGtmEvent({
    event: 'form_submitted_unverfied',
    ...payload,
  });

  if (!goingToBankVerification) {
    return;
  }

  try {
    sessionStorage.setItem(PENDING_BANK_VERIFICATION_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage may be unavailable
  }
};

export const trackFormSubmittedVerified = (): void => {
  let pending: {
    product_type?: string;
    conversion_value?: number;
    currency?: string;
    bank_id?: string;
  } | null = null;

  try {
    const raw = sessionStorage.getItem(PENDING_BANK_VERIFICATION_KEY);
    if (raw) {
      pending = JSON.parse(raw);
      sessionStorage.removeItem(PENDING_BANK_VERIFICATION_KEY);
    }
  } catch {
    // ignore parse/storage errors
  }

  if (!pending) {
    return;
  }

  pushGtmEvent({
    event: 'form_submitted_verfied',
    product_type: pending.product_type || '',
    conversion_value: pending.conversion_value || 0,
    currency: pending.currency || 'EUR',
    bank_authorization: true,
    bank_id: pending.bank_id || '',
  });
};

export const trackFormSubmitError = (
  formData: { loanType?: string; loanAmount?: number | string },
  error?: string
): void => {
  pushGtmEvent({
    event: 'form_submit_error',
    ...getLoanFormGtmPayload(formData),
    error_message: error || 'unknown_error',
  });
};
